import { Document, Packer, Paragraph, TextRun, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';

// Citation format functions
const formatAPA = (article) => {
  const authors = article.authors?.map(author => author.name).join(', ') || 'Unknown Author';
  const year = article.publication_year || 'n.d.';
  const title = article.title || 'Untitled';
  const journal = article.journal_name || '';
  
  let citation = `${authors} (${year}). ${title}.`;
  if (journal) {
    citation += ` ${journal}.`;
  }
  if (article.doi) {
    citation += ` https://doi.org/${article.doi}`;
  } else if (article.landing_page_url) {
    citation += ` ${article.landing_page_url}`;
  }
  
  return citation;
};

const formatMLA = (article) => {
  const authors = article.authors?.map(author => author.name).join(', ') || 'Unknown Author';
  const title = article.title || 'Untitled';
  const journal = article.journal_name || '';
  const year = article.publication_year || '';
  
  let citation = `${authors}. "${title}."`;
  if (journal) {
    citation += ` ${journal},`;
  }
  if (year) {
    citation += ` ${year}.`;
  }
  if (article.landing_page_url) {
    citation += ` Web.`;
  }
  
  return citation;
};

const formatChicago = (article) => {
  const authors = article.authors?.map(author => author.name).join(', ') || 'Unknown Author';
  const title = article.title || 'Untitled';
  const journal = article.journal_name || '';
  const year = article.publication_year || '';
  
  let citation = `${authors}. "${title}."`;
  if (journal && year) {
    citation += ` ${journal} (${year}).`;
  } else if (journal) {
    citation += ` ${journal}.`;
  } else if (year) {
    citation += ` ${year}.`;
  }
  if (article.doi) {
    citation += ` https://doi.org/${article.doi}`;
  }
  
  return citation;
};

// Export function for bibliography
export const exportBibliographyToWord = async (articles, format = 'APA', filename = 'Bibliography') => {
  try {
    if (!articles || articles.length === 0) {
      throw new Error('No articles to export');
    }

    // Format selection
    let formatFunction;
    let formatTitle;
    
    switch (format.toUpperCase()) {
      case 'MLA':
        formatFunction = formatMLA;
        formatTitle = 'MLA Format';
        break;
      case 'CHICAGO':
        formatFunction = formatChicago;
        formatTitle = 'Chicago Format';
        break;
      case 'APA':
      default:
        formatFunction = formatAPA;
        formatTitle = 'APA Format';
        break;
    }

    // Create document paragraphs
    const paragraphs = [
      // Title
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({
            text: 'Bibliography',
            bold: true,
            size: 32,
          }),
        ],
      }),
      // Format subtitle
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({
            text: formatTitle,
            size: 24,
            italics: true,
          }),
        ],
      }),
      // Empty line
      new Paragraph(''),
    ];

    // Sort articles alphabetically by first author's last name or title
    const sortedArticles = [...articles].sort((a, b) => {
      const aText = a.authors?.[0]?.name || a.title || '';
      const bText = b.authors?.[0]?.name || b.title || '';
      return aText.localeCompare(bText);
    });

    // Add each citation
    sortedArticles.forEach((article, index) => {
      const citation = formatFunction(article);
      
      paragraphs.push(
        new Paragraph({
          children: [
            new TextRun({
              text: citation,
              size: 22,
            }),
          ],
          spacing: {
            after: 200, // Add space after each citation
          },
        })
      );
    });

    // Add summary information
    paragraphs.push(
      new Paragraph(''),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({
            text: `Total Articles: ${articles.length}`,
            size: 20,
            italics: true,
          }),
        ],
      }),
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({
            text: `Generated: ${new Date().toLocaleDateString()}`,
            size: 18,
            color: '666666',
          }),
        ],
      })
    );

    // Create document
    const doc = new Document({
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: 1440,    // 1 inch in twips
                right: 1440,
                bottom: 1440,
                left: 1440,
              },
            },
          },
          children: paragraphs,
        },
      ],
    });

    // Export the document
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${filename}_${format}.docx`);
    
    return { success: true, count: articles.length };
  } catch (error) {
    console.error('Error exporting bibliography:', error);
    throw error;
  }
};

// Available export formats
export const EXPORT_FORMATS = [
  { value: 'APA', label: 'APA Style' },
  { value: 'MLA', label: 'MLA Style' },
  { value: 'CHICAGO', label: 'Chicago Style' },
];